/**
 * YearlyAgendaView Component
 * Lists all days with events in a year as an agenda
 * Shows event details with airplane colors
 */

import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Spin, Empty, Badge } from 'antd';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { useEvents } from '@/hooks/useCalendars';
import type { CalendarEvent, Resource } from '@/api/types/calendars.types';
import './YearlyAgendaView.css';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface YearlyAgendaViewProps {
  /** Year to display */
  year: number;
  /** Calendar ID */
  calendarId: string;
  /** Selected resource IDs for filtering */
  selectedResourceIds?: string[];
  /** All resources (for color and name mapping) */
  allResources?: Resource[];
  /** Callback when an event is clicked */
  onEventClick?: (event: CalendarEvent) => void;
  /** Callback when a day is clicked */
  onDayClick?: (date: Date) => void;
}

interface GroupedEvents {
  date: string;
  displayDate: string;
  events: CalendarEvent[];
}

/**
 * Get resource color
 */
const getResourceColor = (
  resourceId: string,
  allResources: Resource[]
): string => {
  const resource = allResources.find((r) => r.id === resourceId);
  return resource?.metadata?.color || '#1890ff';
};

/**
 * Get resource name
 */
const getResourceName = (
  resourceId: string,
  allResources: Resource[]
): string => {
  const resource = allResources.find((r) => r.id === resourceId);
  return resource?.resource_name || 'Unknown';
};

/**
 * Format event time range
 */
const formatTimeRange = (
  start: string,
  end: string,
  userTimezone: string
): string => {
  const startTime = dayjs.tz(start, userTimezone);
  const endTime = dayjs.tz(end, userTimezone);

  return `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')}`;
};

/**
 * Day Group Component
 */
interface DayGroupProps {
  group: GroupedEvents;
  allResources: Resource[];
  userTimezone: string;
  onEventClick?: (event: CalendarEvent) => void;
  onDayClick?: (date: Date) => void;
}

const DayGroup: React.FC<DayGroupProps> = ({
  group,
  allResources,
  userTimezone,
  onEventClick,
  onDayClick,
}) => {
  return (
    <div className="agenda-day-group">
      <div
        className="agenda-day-header"
        onClick={() => onDayClick?.(new Date(group.date))}
      >
        <div className="agenda-day-date">{group.displayDate}</div>
        <Badge
          count={group.events.length}
          style={{ backgroundColor: '#52c41a' }}
        />
      </div>

      <div className="agenda-events-list">
        {group.events.map((event) => {
          const airplaneResource = event.resources?.find((r) =>
            allResources.find(
              (res) => res.id === r && res.resource_type === 'AIRPLANE'
            )
          );
          const airplaneColor = airplaneResource
            ? getResourceColor(airplaneResource, allResources)
            : '#1890ff';
          const airplaneName = airplaneResource
            ? getResourceName(airplaneResource, allResources)
            : 'No airplane';

          return (
            <div
              key={event.id}
              className="agenda-event-item"
              onClick={() => onEventClick?.(event)}
            >
              <div className="agenda-event-time">
                {formatTimeRange(
                  event.start_time,
                  event.end_time,
                  userTimezone
                )}
              </div>

              <div className="agenda-event-content">
                <div className="agenda-event-airplane">
                  <span
                    className="agenda-airplane-indicator"
                    style={{ backgroundColor: airplaneColor }}
                  />
                  <span className="agenda-airplane-name">{airplaneName}</span>
                </div>

                <div className="agenda-event-title">
                  {event.event_name || 'Flight'}
                </div>

                {event.description && (
                  <div className="agenda-event-description">
                    {event.description}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Main YearlyAgendaView Component
 */
export const YearlyAgendaView: React.FC<YearlyAgendaViewProps> = ({
  year,
  calendarId,
  selectedResourceIds,
  allResources = [],
  onEventClick,
  onDayClick,
}) => {
  const { timezone: userTimezone } = useUserTimezone();

  // Fetch all events for the year
  const startDate = `${year}-01-01`;
  const endDate = `${year}-12-31`;

  const {
    data: events,
    isLoading,
    error,
  } = useEvents(calendarId, {
    start_date: startDate,
    end_date: endDate,
    resource_ids: selectedResourceIds,
  });

  // Group events by day
  const groupedEvents = useMemo(() => {
    if (!events || events.length === 0) return [];

    const groups: Record<string, CalendarEvent[]> = {};

    events.forEach((event) => {
      const eventDate = dayjs
        .tz(event.start_time, userTimezone)
        .format('YYYY-MM-DD');
      if (!groups[eventDate]) {
        groups[eventDate] = [];
      }
      groups[eventDate].push(event);
    });

    // Convert to array and sort
    const sortedGroups: GroupedEvents[] = Object.entries(groups)
      .map(([date, dayEvents]) => ({
        date,
        displayDate: dayjs.tz(date, userTimezone).format('dddd, D. MMMM YYYY'),
        events: dayEvents.sort(
          (a, b) =>
            dayjs.tz(a.start_time, userTimezone).valueOf() -
            dayjs.tz(b.start_time, userTimezone).valueOf()
        ),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return sortedGroups;
  }, [events, userTimezone]);

  if (isLoading) {
    return (
      <div className="agenda-view-loading">
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading {year} agenda...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agenda-view-empty">
        <Empty description="Failed to load events" />
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="agenda-view-empty">
        <Empty description={`No events in ${year}`} />
      </div>
    );
  }

  return (
    <div className="yearly-agenda-view">
      <div className="agenda-header">
        <h2>{year} Agenda</h2>
        <div className="agenda-stats">
          {groupedEvents.length} days with events • {events.length} total events
        </div>
      </div>

      <div className="agenda-content">
        {groupedEvents.map((group) => (
          <DayGroup
            key={group.date}
            group={group}
            allResources={allResources}
            userTimezone={userTimezone}
            onEventClick={onEventClick}
            onDayClick={onDayClick}
          />
        ))}
      </div>
    </div>
  );
};
